import { useCallback, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { Bell, ChevronsLeft, ChevronsRight, ChevronsUpDown, Globe, LogOut, MenuIcon, Play, Search, User } from 'lucide-react'
import logoDark from '@/assets/vidchops-logo-dark.png'
import { useAuth } from '@/features/auth/useAuth'
import { roleLabel, isCustomer } from '@/features/auth/roles'
import { fullName } from '@/lib/format'
import { readStorage, STORAGE_KEYS, writeStorage } from '@/lib/storage'
import { Avatar, IconButton, Input, Menu, MenuDivider, MenuItem, MenuLabel } from '@/components/ui'
import { useNavBadges } from '@/features/notifications/useNavBadges'
import { NotificationsMenu } from '@/features/notifications/NotificationsMenu'
import { useWebsiteSwitch } from '@/features/auth/useWebsiteSwitch'
import { usePeoplePreload } from '@/features/people/usePeoplePreload'
import { findNavItem, navGroupsFor } from './navigation'
import { TopbarSlotsContext, type TopbarSlots } from './topbar'
import styles from './AppShell.module.scss'

export function AppShell() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(() => readStorage(STORAGE_KEYS.sidebarCollapsed) === '1')
  // The mobile drawer remembers the path it was opened on, so navigating closes it without an effect.
  const [mobileOpenedAt, setMobileOpenedAt] = useState<string | null>(null)
  const mobileOpen = mobileOpenedAt === location.pathname
  const openMobile = () => setMobileOpenedAt(location.pathname)
  const closeMobile = () => setMobileOpenedAt(null)
  const [slots, setSlots] = useState<TopbarSlots>({ title: null, actions: null })
  const setTitleSlot = useCallback((el: HTMLDivElement | null) => setSlots((s) => (s.title === el ? s : { ...s, title: el })), [])
  const setActionsSlot = useCallback((el: HTMLDivElement | null) => setSlots((s) => (s.actions === el ? s : { ...s, actions: el })), [])
  const badges = useNavBadges()
  const { switchTo, switching } = useWebsiteSwitch()
  // Team and customer lists load once here and are served from the cache on the People pages.
  usePeoplePreload()

  function toggleCollapsed() {
    setCollapsed((c) => {
      writeStorage(STORAGE_KEYS.sidebarCollapsed, c ? '0' : '1')
      return !c
    })
  }

  const groups = navGroupsFor(user)
  const current = findNavItem(location.pathname)
  const currentWebsite = user?.websites?.find((w) => w.is_current)
  const otherWebsites = (user?.websites ?? []).filter((w) => !w.is_current)
  // A white-label portal shows its own logo and name; everything else shows the VidChops wordmark.
  const whiteLabel = !!currentWebsite && !!currentWebsite.logo && currentWebsite.name.toLowerCase() !== 'vidchops'

  return (
    <div className={styles.shell}>
      {mobileOpen && <div className={styles.overlay} onClick={closeMobile} aria-hidden="true" />}
      <aside className={clsx(styles.sidebar, collapsed && styles.collapsed, mobileOpen && styles.sidebarOpen)} aria-label="Primary">
        <div className={styles.sidebarInner}>
          <div className={styles.brandRow}>
            <Link to="/" className={styles.brand}>
              {whiteLabel ? (
                <>
                  <img src={currentWebsite.logo ?? undefined} alt="" className={styles.logoImg} />
                  <span className={styles.brandName}>{currentWebsite.name}</span>
                </>
              ) : (
                <>
                  {/* Full wordmark when expanded, a compact brand tile when collapsed (CSS switches them). */}
                  <img src={logoDark} alt="VidChops" className={styles.brandLogo} />
                  <span className={styles.brandMark} aria-hidden="true">
                    <Play size={16} fill="currentColor" />
                  </span>
                </>
              )}
            </Link>
            <IconButton
              label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              size="sm"
              className={styles.collapseBtn}
              onClick={toggleCollapsed}
            >
              {collapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
            </IconButton>
          </div>

          <nav className={styles.nav}>
            {groups.map((group) => (
              <div key={group.title}>
                <div className={styles.groupTitle}>{group.title}</div>
                {group.items.map((item) => {
                  const count = item.badge ? badges[item.badge] : undefined
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => clsx(styles.navItem, isActive && styles.navActive)}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon />
                      <span className={styles.navLabel}>{item.label}</span>
                      {count ? <span className={styles.navBadge}>{count}</span> : null}
                    </NavLink>
                  )
                })}
              </div>
            ))}
          </nav>

          {/* The whole user card is the menu trigger, so it still works when the sidebar is collapsed.
              The menu opens upwards: the card sits at the bottom of the screen. */}
          <Menu
            className={styles.userMenu}
            align="left"
            placement="top"
            trigger={({ toggle, open }) => (
              <button
                type="button"
                className={styles.userCard}
                onClick={toggle}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={open}
                title={collapsed ? fullName(user) : undefined}
              >
                <Avatar name={fullName(user)} src={user?.profile_img} size="sm" />
                <span className={styles.userText}>
                  <span className={styles.userName}>{fullName(user)}</span>
                  <span className={styles.userRole}>{roleLabel(user?.client_type)}</span>
                </span>
                <ChevronsUpDown size={14} className={styles.userChevron} aria-hidden="true" />
              </button>
            )}
          >
            {(close) => (
              <>
                <MenuItem icon={<User />} to="/profile" onClick={close}>
                  My profile
                </MenuItem>
                {!isCustomer(user) && otherWebsites.length > 0 && (
                  <>
                    <MenuDivider />
                    <MenuLabel>Switch website</MenuLabel>
                    {otherWebsites.map((w) => (
                      <MenuItem key={w.id} icon={<Globe />} disabled={switching} onClick={() => void switchTo(w.id)}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </>
                )}
                <MenuDivider />
                <MenuItem
                  icon={<LogOut />}
                  danger
                  onClick={() => {
                    close()
                    void logout().then(() => navigate('/login'))
                  }}
                >
                  Log out
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.topbarInner}>
            <IconButton label="Open navigation" className={styles.menuBtn} onClick={openMobile}>
              <MenuIcon size={18} />
            </IconButton>
            <div className={styles.topbarTitles}>
              <div ref={setTitleSlot} className={styles.titleSlot} />
              <div className={styles.titleFallback}>
                <div className={styles.topbarCrumb}>{crumbFor(location.pathname, current?.label)}</div>
                <div className={styles.topbarTitle}>{current?.label ?? titleFor(location.pathname)}</div>
              </div>
            </div>
            <GlobalSearch className={styles.search} />
            <div className={styles.topbarActions}>
              <NotificationsMenu
                trigger={(toggle, unseen) => (
                  <span className={styles.bellWrap}>
                    <IconButton label="Notifications" variant="secondary" onClick={toggle}>
                      <Bell size={17} />
                    </IconButton>
                    {unseen > 0 && <span className={styles.bellDot}>{unseen > 99 ? '99+' : unseen}</span>}
                  </span>
                )}
              />
              <div ref={setActionsSlot} className={styles.actionsSlot} />
            </div>
          </div>
        </header>
        <main className={styles.content}>
          <TopbarSlotsContext.Provider value={slots}>
            <Outlet />
          </TopbarSlotsContext.Provider>
        </main>
      </div>
    </div>
  )
}

function crumbFor(pathname: string, label?: string): string {
  const section = pathname.startsWith('/studio')
    ? 'AI Studio'
    : ['/customers', '/team', '/ratings'].some((p) => pathname.startsWith(p))
      ? 'People'
      : ['/inbox', '/handbook', '/settings'].some((p) => pathname.startsWith(p))
        ? 'Resources'
        : ['/new-request', '/upload', '/archived', '/export', '/cards'].some((p) => pathname.startsWith(p))
          ? 'Production'
          : 'Workspace'
  return label ? `${section} / ${label}` : section
}

function titleFor(pathname: string): string {
  if (pathname.startsWith('/cards/')) return 'Card Details'
  if (pathname.startsWith('/customers/')) return 'Customer Details'
  if (pathname.startsWith('/team/')) return 'Team Member'
  if (pathname.startsWith('/profile')) return 'My Profile'
  if (pathname.startsWith('/notifications')) return 'Notifications'
  return 'VidChops'
}

function GlobalSearch({ className }: { className?: string }) {
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  return (
    <form
      className={className}
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        const q = value.trim()
        navigate(q ? `/?q=${encodeURIComponent(q)}` : '/')
      }}
    >
      <Input
        size="sm"
        placeholder="Search cards…"
        leftIcon={<Search />}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Search cards"
      />
    </form>
  )
}

export function PageContainer({ children }: { children: ReactNode }) {
  return <div>{children}</div>
}
